/* Post content enrichment. UMD: window.RohitEnrich + window.initPostEnrichments
   in the browser; module.exports in node (tooling/check-post-enrichments.mjs).
   Pure helpers are unit-tested; init() enhances the .gh-content DOM. */
(function (root, factory) {
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
    if (root) { root.RohitEnrich = api; root.initPostEnrichments = api.init; }
})(typeof self !== 'undefined' ? self : (typeof global !== 'undefined' ? global : this), function () {

    var IMG_RE = /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i;

    /* Reduced-motion helpers (ES5, browser-safe) */
    function prefersReducedMotion() {
        return typeof window !== 'undefined' && window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    function scrollBehavior() { return prefersReducedMotion() ? 'auto' : 'smooth'; }

    function slug(t) {
        return String(t == null ? '' : t).toLowerCase()
            .replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
    }

    function fmt(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            /* image/GIF FIRST so the link rule below can't match the [alt](url)
               half of an ![alt](url). Sizing is CSS-driven (#enrich-tip .enrich-tip-img)
               so any source — tall, wide, animated GIF — is capped to fit the popup.
               An &-bearing URL is already escaped to &amp;, which is the correct form
               inside a src attribute (same as the link rule relies on). */
            .replace(/!\[([^\]]*?)\]\(([^)]+?)\)/g, '<img src="$2" alt="$1" class="enrich-tip-img" loading="lazy">')
            .replace(/\*\*([^*]+?)\*\*/g, '<b>$1</b>')
            .replace(/\*([^*]+?)\*/g, '<i>$1</i>')
            .replace(/\[([^\]]+?)\]\(([^)]+?)\)/g, '<a href="$2" style="color:var(--enrich-deep)">$1</a>');
    }

    function decode(s) { try { return decodeURIComponent(s); } catch (e) { return s; } }

    function parseTrigger(href) {
        var h = String(href == null ? '' : href);
        if (h.indexOf('#tip:') === 0) { return { type: 'tip', value: decode(h.slice(5)), caption: '' }; }
        if (h.indexOf('#fn:') === 0) { return { type: 'fn', value: decode(h.slice(4)), caption: '' }; }
        var capIdx = h.indexOf('#cap=');
        var isImg = IMG_RE.test(h) || capIdx !== -1;
        if (isImg) {
            var url = capIdx !== -1 ? h.slice(0, capIdx) : h;
            var caption = capIdx !== -1 ? decode(h.slice(capIdx + 5)) : '';
            return { type: 'image', value: url, caption: caption };
        }
        return { type: null, value: '', caption: '' };
    }

    /* ------------------------------------------------------------------ *
     *  Image overlay — state machine + DOM (built once per page load)    *
     * ------------------------------------------------------------------ */

    var _ov = null, _ovImg = null, _ovCap = null, _ovFrame = null;
    var _ovActive = null, _ovDismissed = false, _ovTriggerHover = false, _ovFrameHover = false, _ovCloseTimer = null;

    function ensureOverlay() {
        if (_ov) return;
        _ov = document.createElement('div');
        _ov.id = 'enrich-ov';
        _ov.setAttribute('role', 'dialog');
        _ov.setAttribute('aria-modal', 'true');
        _ov.setAttribute('aria-label', 'Image preview');

        var xBtn = document.createElement('span');
        xBtn.className = 'enrich-x';
        xBtn.innerHTML = '&times;';
        xBtn.setAttribute('role', 'button');
        xBtn.setAttribute('tabindex', '0');
        xBtn.setAttribute('aria-label', 'Close');
        xBtn.addEventListener('click', function () { ovDismiss(); });
        xBtn.addEventListener('keydown', function (e) {
            if (e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); ovDismiss(); }
        });

        _ovFrame = document.createElement('div');
        _ovFrame.className = 'enrich-frame';

        _ovImg = document.createElement('img');
        _ovImg.alt = '';

        _ovCap = document.createElement('div');
        _ovCap.className = 'enrich-cap';

        var hint = document.createElement('div');
        hint.className = 'enrich-hint';
        hint.textContent = 'click image to expand fully · click outside or Esc to close';

        _ovFrame.appendChild(_ovImg);
        _ovFrame.appendChild(_ovCap);
        _ovFrame.appendChild(hint);
        _ov.appendChild(xBtn);
        _ov.appendChild(_ovFrame);
        document.body.appendChild(_ov);

        _ovFrame.addEventListener('click', function (e) {
            if (e.target === xBtn || xBtn.contains(e.target)) return;
            _ov.classList.toggle('zoom');
        });
        /* Image is click-to-open / click-to-close (owner): NO hover-close. The
           overlay closes only via ×, Esc, or a click/tap anywhere outside the
           frame (handled below). Moving the mouse off the image no longer closes it. */

        document.addEventListener('click', function (e) {
            if (!_ov.classList.contains('show')) return;
            if (_ovFrame.contains(e.target) || e.target === _ovFrame) return;
            if (_ovActive && (_ovActive === e.target || _ovActive.contains(e.target))) return;
            e.preventDefault();
            ovDismiss();
        }, true);

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') { ovDismiss(); }
        });
    }

    function ovOpen(triggerEl, imgUrl, captionHtml) {
        if (_ovDismissed && triggerEl === _ovActive) return;
        _ovActive = triggerEl;
        _ovDismissed = false;
        _ovImg.src = imgUrl;
        _ovCap.innerHTML = captionHtml;
        _ov.classList.remove('zoom');
        _ov.classList.add('show');
    }

    function ovHardClose() {
        if (!_ov) return;
        _ov.classList.remove('show', 'zoom');
    }

    function ovDismiss() {
        ovHardClose();
        _ovDismissed = true;
    }

    function ovScheduleClose() {
        clearTimeout(_ovCloseTimer);
        _ovCloseTimer = setTimeout(function () {
            if (!_ovTriggerHover && !_ovFrameHover) ovHardClose();
        }, 180);
    }

    /* ------------------------------------------------------------------ *
     *  Shared popup + selection guard — reused by Tasks 3 and 4          *
     * ------------------------------------------------------------------ */

    var _tip = null;

    function ensureShared() {
        if (_tip) return _tip;
        _tip = document.getElementById('enrich-tip');
        if (!_tip) {
            _tip = document.createElement('div');
            _tip.id = 'enrich-tip';
            _tip.setAttribute('role', 'tooltip');
            document.body.appendChild(_tip);
        }
        return _tip;
    }

    var _pointerDown = false;
    /* F7: on touch devices a tap fires a synthetic mouseenter THEN click together;
       the mouseenter would show the tooltip and the click would immediately toggle
       it back off, so the first tap appeared to "do nothing" (only highlight the
       word) and a second tap was needed. Once any touch is seen, treat the device
       as touch: hover-open popups no-op and the tap is handled by click alone, so
       the tooltip opens on the FIRST tap. A pure mouse never sets this flag. */
    var _isTouch = false;
    if (typeof document !== 'undefined') {
        document.addEventListener('mousedown', function () { _pointerDown = true; });
        window.addEventListener('mouseup', function () { _pointerDown = false; });
        document.addEventListener('touchstart', function () { _isTouch = true; }, true);
    }

    /* Mobile tap handling. iOS/Android withhold the synthetic click on the FIRST
       tap of an element that has :hover styles (the "two-tap" problem) — which is
       why the tooltip needed a second tap and footnote pills (href-less <a>) often
       didn't fire click at all. We bind the action to touchend directly and
       preventDefault to cancel the synthetic mouse+click sequence, so a single tap
       always works. _lastTap then suppresses any click that still slips through. */
    var _lastTap = 0;
    function tapNow() { return (new Date()).getTime(); }
    function onTap(el, fn) {
        var sx = 0, sy = 0, moved = false;
        el.addEventListener('touchstart', function (e) {
            var t = e.touches && e.touches[0]; if (!t) { return; }
            sx = t.clientX; sy = t.clientY; moved = false;
        }, { passive: true });
        el.addEventListener('touchmove', function (e) {
            var t = e.touches && e.touches[0]; if (!t) { return; }
            if (Math.abs(t.clientX - sx) > 10 || Math.abs(t.clientY - sy) > 10) { moved = true; }
        }, { passive: true });
        el.addEventListener('touchend', function (e) {
            if (moved) { return; }                      /* it was a scroll/drag, not a tap */
            var sel = window.getSelection && window.getSelection();
            if (sel && String(sel).length > 0) { return; } /* user is selecting text */
            e.preventDefault();                          /* cancel synthetic click + double-tap */
            _lastTap = tapNow();
            fn(e);
        });
    }
    function tapJustHandled() { return _lastTap && (tapNow() - _lastTap) < 700; }

    function isSelecting() {
        var s = window.getSelection && window.getSelection();
        return _pointerDown || (s && String(s).length > 0);
    }

    function popTip(html, rect) {
        var tip = ensureShared();
        tip.innerHTML = html;
        tip.classList.add('show');
        var x = Math.min(rect.left, window.innerWidth - 330);
        var y = rect.bottom + 8;
        if (y + tip.offsetHeight > window.innerHeight) { y = rect.top - tip.offsetHeight - 8; }
        tip.style.left = Math.max(10, x) + 'px';
        tip.style.top = Math.max(10, y) + 'px';
    }

    function hideTip() {
        var tip = ensureShared();
        tip.classList.remove('show');
    }

    /* ------------------------------------------------------------------ *
     *  init — scans .gh-content, wires tooltip triggers                  *
     * ------------------------------------------------------------------ */

    function init() {
        ensureShared();
        ensureOverlay();

        var content = document.querySelector('.gh-content');
        if (!content) return;

        /* ---------------------------------------------------------------- *
         *  Bare-table normaliser. Tables authored via a markdown card, a   *
         *  plain HTML <table>, or an Admin-API HTML push arrive WITHOUT     *
         *  the theme's .gh-table scroll wrapper, so screen.css can't style  *
         *  them. Wrap each such table in <div class="gh-table gh-table--    *
         *  auto"> so it reuses the existing (tested) .gh-table styling;     *
         *  the --auto modifier lets prose cells wrap and scrolls only when  *
         *  genuinely too wide. Idempotent: a table already inside .gh-table *
         *  (hand-wrapped, or a re-run of init) is skipped. Runs in the      *
         *  browser only — the stored HTML stays a clean <table>, which is   *
         *  what the mobile app reads from the Content API.                  *
         * ---------------------------------------------------------------- */
        wrapBareTables(content, document);

        /* ---------------------------------------------------------------- *
         *  Task 5 — Step A: assign stable ids to H1-H6 BEFORE breadcrumb  *
         *  reads them. ensureHeadingIds skips any id that isn't bc-h-*     *
         *  (breadcrumb's fallback prefix), so ids that already exist are   *
         *  preserved; bc-h-* ids from a previous breadcrumb run are        *
         *  replaced with slug-based ones.                                  *
         * ---------------------------------------------------------------- */
        ensureHeadingIds(content.querySelectorAll('h1, h2, h3, h4, h5, h6'));

        /* ---------------------------------------------------------------- *
         *  Task 5 — Step B: build TOC for each <div class="toc"> marker   *
         * ---------------------------------------------------------------- */
        (function () {
            var markers = content.querySelectorAll('div.toc');
            if (!markers.length) { return; }

            /* Collect H1-H6 headings once (ids now set by ensureHeadingIds) */
            var headingNodes = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
            var rawHeadings = [];
            for (var hi = 0; hi < headingNodes.length; hi++) {
                var hn = headingNodes[hi];
                rawHeadings.push({
                    level: parseInt(hn.tagName.charAt(1), 10),
                    text: hn.textContent.trim(),
                    id: hn.id
                });
            }
            var model = tocModel(rawHeadings);
            if (!model.length) { return; }

            for (var mi = 0; mi < markers.length; mi++) {
                (function (marker) {
                    /* Build <nav class="enrich-toc"> */
                    var nav = document.createElement('nav');
                    nav.className = 'enrich-toc';

                    var hd = document.createElement('div');
                    hd.className = 'enrich-toc-h';
                    hd.textContent = 'On this page';
                    nav.appendChild(hd);

                    var ul = document.createElement('ul');
                    ul.className = 'enrich-toc-list';
                    var items = []; /* [{h: headingNode, a: linkEl}] */

                    for (var ti = 0; ti < model.length; ti++) {
                        (function (entry) {
                            var targetNode = document.getElementById(entry.id);
                            var li = document.createElement('li');
                            var a = document.createElement('a');
                            a.href = '#' + entry.id;
                            a.textContent = entry.text;
                            a.className = 'enrich-toc-lv' + entry.depth;
                            a.addEventListener('click', function (e) {
                                e.preventDefault();
                                var t = document.getElementById(entry.id);
                                if (t) { t.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); }
                            });
                            li.appendChild(a);
                            ul.appendChild(li);
                            if (targetNode) { items.push({ h: targetNode, a: a }); }
                        })(model[ti]);
                    }

                    nav.appendChild(ul);

                    /* Replace the marker div's contents with the nav */
                    while (marker.firstChild) { marker.removeChild(marker.firstChild); }
                    marker.appendChild(nav);

                    /* Owner: TOC highlight is HOVER-ONLY. The previous
                       IntersectionObserver auto-highlighted the section in view,
                       which left the first item persistently highlighted (and
                       misleading). Removed — :hover is the only highlight now. */
                })(markers[mi]);
            }
        })();

        var anchors = content.querySelectorAll('a[href]');
        for (var i = 0; i < anchors.length; i++) {
            (function (a) {
                var href = a.getAttribute('href');
                var parsed = parseTrigger(href);

                if (parsed.type === 'tip') {
                    var value = parsed.value;
                    a.classList.add('enrich-tip-trigger');
                    a.setAttribute('tabindex', '0');

                    a.addEventListener('mouseenter', function () {
                        if (_isTouch) { return; } /* F7: tap handled by click, not synthetic hover */
                        if (!isSelecting()) { popTip(fmt(value), a.getBoundingClientRect()); }
                    });
                    a.addEventListener('mouseleave', hideTip);
                    a.addEventListener('focus', function () {
                        popTip(fmt(value), a.getBoundingClientRect());
                    });
                    a.addEventListener('blur', hideTip);
                    function tipToggle() {
                        if (isSelecting()) return;
                        var tip = ensureShared();
                        if (tip.classList.contains('show')) { hideTip(); }
                        else { popTip(fmt(value), a.getBoundingClientRect()); }
                    }
                    a.addEventListener('click', function (e) {
                        e.preventDefault();
                        if (tapJustHandled()) return; /* tap already handled by onTap */
                        tipToggle();
                    });
                    onTap(a, tipToggle); /* mobile: open on FIRST tap */
                    a.addEventListener('keydown', function (e) {
                        if (e.key === 'Escape') { hideTip(); }
                    });
                }

                if (parsed.type === 'image') {
                    var imgUrl = parsed.value;
                    var captionHtml = fmt(parsed.caption);
                    a.classList.add('enrich-img-trigger');
                    a.setAttribute('tabindex', '0');
                    a.setAttribute('role', 'button');
                    a.setAttribute('aria-label', 'View image' + (parsed.caption ? ': ' + parsed.caption : ''));

                    /* F2: image opens on CLICK, not hover. The image-icon cursor is the
                       hover affordance (CSS). Hover only PRELOADS so the click is instant. */
                    var _preloaded = false;
                    a.addEventListener('mouseenter', function () {
                        if (!_preloaded) { var tmp = new Image(); tmp.src = imgUrl; _preloaded = true; }
                    });

                    function imgActivate() {
                        if (isSelecting()) { return; }
                        if (_ov && _ov.classList.contains('show') && _ovActive === a) {
                            ovDismiss();
                        } else {
                            _ovDismissed = false;
                            ovOpen(a, imgUrl, captionHtml);
                        }
                    }
                    a.addEventListener('click', function (e) {
                        e.preventDefault();
                        if (tapJustHandled()) return; /* tap already handled by onTap */
                        imgActivate();
                    });
                    onTap(a, imgActivate); /* mobile: open on FIRST tap */
                    a.addEventListener('keydown', function (e) {
                        if (e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); imgActivate(); }
                        else if (e.key === 'Escape') { ovDismiss(); }
                    });
                }

            })(anchors[i]);
        }

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') { hideTip(); }
        });

        if (!init._outsideTapWired) {
            init._outsideTapWired = true;
            document.addEventListener('click', function (e) {
                var tip = ensureShared();
                if (!tip.classList.contains('show')) return;
                var t = e.target;
                if (t && (t.classList.contains('enrich-tip-trigger') || tip.contains(t) || t === tip)) return;
                hideTip();
            });
        }

        /* ---------------------------------------------------------------- *
         *  Task 4: Footnotes — build loop + sidenote / narrow-pin          *
         * ---------------------------------------------------------------- */

        /* Constants for margin geometry (matches --content-width = 720px) */
        var FN_COL = 720;
        var FN_MINNOTE = 240;
        var FN_GAP = 24;

        /* Per-page state */
        var fnNotes = [];         /* [{n, note, pill, source}]           */
        var fnOpenNote = null;    /* {n, el}  — current sidenote or null */
        var fnPinnedNarrow = null; /* pill <a> element or null           */

        /* ---- geometry helpers ----------------------------------------- */
        function fnMarginRoom() { return (window.innerWidth - FN_COL) / 2; }
        function fnIsDesktopMargin() { return fnMarginRoom() >= FN_MINNOTE + 12; }

        /* ---- flash: highlight connected element briefly ---------------- */
        function fnFlash(el) {
            if (!el) return;
            el.classList.add('enrich-fn-flash');
            setTimeout(function () { el.classList.remove('enrich-fn-flash'); }, 1300);
        }

        /* ---- sidenote (desktop right-margin) --------------------------- */
        function fnCloseSidenote() {
            if (!fnOpenNote) return;
            fnOpenNote.el.parentNode && fnOpenNote.el.parentNode.removeChild(fnOpenNote.el);
            fnNotes[fnOpenNote.n - 1].pill.classList.remove('open');
            fnOpenNote = null;
        }

        function fnOpenSidenote(item) {
            hideTip();
            fnCloseSidenote();
            item.pill.classList.add('open');

            var el = document.createElement('aside');
            el.className = 'enrich-sidenote';
            el.innerHTML = '<span class="enrich-sn-x">&times;</span>' +
                '<div><span class="enrich-sn-num">' + item.n + '.</span>' + fmt(item.note) + '</div>' +
                '<a class="enrich-sn-tolist" href="#enrich-fn-' + item.n + '">see in list &#8595;</a>';

            content.appendChild(el);

            var w = Math.min(320, fnMarginRoom() - FN_GAP);
            el.style.width = w + 'px';
            el.style.left = (FN_COL + (fnMarginRoom() - w) / 2 + FN_GAP / 2) + 'px';

            var contentRect = content.getBoundingClientRect();
            var refTop = item.pill.getBoundingClientRect().top + window.scrollY -
                (contentRect.top + window.scrollY);
            el.style.top = Math.max(0, refTop) + 'px';

            var snX = el.querySelector('.enrich-sn-x');
            snX.setAttribute('role', 'button');
            snX.setAttribute('tabindex', '0');
            snX.setAttribute('aria-label', 'Close');
            snX.addEventListener('click', fnCloseSidenote);
            snX.addEventListener('keydown', function (e) {
                if (e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); fnCloseSidenote(); }
            });
            el.querySelector('.enrich-sn-tolist').addEventListener('click', function (e) {
                e.preventDefault();
                fnCloseSidenote(); /* F5: close the open margin sidenote */
                var li = document.getElementById('enrich-fn-' + item.n);
                fnFlash(li);
                li.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); /* F4 */
            });

            fnOpenNote = { n: item.n, el: el };
        }

        /* ---- narrow-pin (mobile inline popup via shared #enrich-tip) --- */
        function fnPinNarrow(item) {
            if (fnPinnedNarrow) {
                var pp = fnPinnedNarrow;
                fnPinnedNarrow = null;
                pp.classList.remove('open');
            }
            if (!item) {
                var tipEl = ensureShared();
                tipEl.classList.remove('enrich-tip-pin');
                hideTip();
                return;
            }
            fnPinnedNarrow = item.pill;
            item.pill.classList.add('open');

            var tip = ensureShared();
            tip.innerHTML = '<span class="enrich-sn-x">&times;</span>' +
                fmt(item.note) +
                ' <a class="enrich-sn-tolist" href="#enrich-fn-' + item.n + '">see in list &#8595;</a>';
            tip.classList.add('show', 'enrich-tip-pin');
            tip.removeAttribute('data-mode');
            tip.dataset.mode = 'pin';

            var r = item.pill.getBoundingClientRect();
            var x = Math.min(r.left, window.innerWidth - 312);
            var y = r.bottom + 8;
            if (y + tip.offsetHeight > window.innerHeight) { y = r.top - tip.offsetHeight - 8; }
            tip.style.left = Math.max(10, x) + 'px';
            tip.style.top = Math.max(10, y) + 'px';

            var pinX = tip.querySelector('.enrich-sn-x');
            pinX.setAttribute('role', 'button');
            pinX.setAttribute('tabindex', '0');
            pinX.setAttribute('aria-label', 'Close');
            pinX.addEventListener('click', function () {
                fnPinNarrow(null);
            });
            pinX.addEventListener('keydown', function (e) {
                if (e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); fnPinNarrow(null); }
            });
            tip.querySelector('.enrich-sn-tolist').addEventListener('click', function (e) {
                e.preventDefault();
                var li = document.getElementById('enrich-fn-' + item.n);
                fnPinNarrow(null);
                fnFlash(li);
                li.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); /* F4 */
            });
        }

        /* ---- one-at-a-time toggle -------------------------------------- */
        function fnToggle(n) {
            var item = fnNotes[n - 1];
            if (fnIsDesktopMargin()) {
                var wasOpen = fnOpenNote && fnOpenNote.n === n;
                fnCloseSidenote();
                if (!wasOpen) { fnOpenSidenote(item); }
            } else {
                if (fnPinnedNarrow === item.pill) {
                    fnPinNarrow(null);
                } else {
                    fnPinNarrow(item);
                }
            }
        }

        /* ---- build loop ----------------------------------------------- */
        (function () {
            var allAnchors = content.querySelectorAll('a[href]');
            var ol = document.createElement('ol');
            var n = 0;

            for (var i = 0; i < allAnchors.length; i++) {
                var a = allAnchors[i];
                var parsed = parseTrigger(a.getAttribute('href'));
                if (parsed.type !== 'fn') { continue; }

                n++;
                var noteText = parsed.value;

                /* Mark the linked text as the anchor / flash target */
                a.addEventListener('click', function (e) { e.preventDefault(); });
                a.removeAttribute('href');
                a.classList.add('enrich-fn-anchor');
                a.id = 'enrich-fnsrc-' + n;

                /* Append the numbered pill right after the anchor */
                var sup = document.createElement('sup');
                sup.className = 'enrich-fnref';
                sup.innerHTML = '<a id="enrich-fnref-' + n + '" data-n="' + n + '">' + n + '</a>';
                a.parentNode.insertBefore(sup, a.nextSibling);

                var pill = sup.querySelector('a');

                /* Collect for toggle/sidenote */
                fnNotes.push({ n: n, note: noteText, pill: pill, source: a });

                /* a11y: make pill focusable and keyboard-operable */
                pill.setAttribute('tabindex', '0');
                pill.setAttribute('role', 'button');
                pill.setAttribute('aria-label', 'Footnote ' + n);

                /* Hover peek — reads note from closure (safe: note is per-iteration var) */
                (function (pillEl, noteVal, pillN) {
                    pillEl.addEventListener('mouseenter', function () {
                        if (_isTouch) { return; } /* F7: tap toggles via click, not synthetic hover peek */
                        if (!pillEl.classList.contains('open')) {
                            var tip = ensureShared();
                            tip.dataset.mode = 'peek';
                            popTip(fmt(noteVal), pillEl.getBoundingClientRect());
                        }
                    });
                    pillEl.addEventListener('mouseleave', function () {
                        if (!fnPinnedNarrow) {
                            var tip = ensureShared();
                            if (tip.dataset.mode === 'peek') { hideTip(); }
                        }
                    });
                    /* Focus shows peek the same way mouseenter does */
                    pillEl.addEventListener('focus', function () {
                        if (!pillEl.classList.contains('open')) {
                            var tip = ensureShared();
                            tip.dataset.mode = 'peek';
                            popTip(fmt(noteVal), pillEl.getBoundingClientRect());
                        }
                    });
                    /* Blur hides peek (matching hover dismissal) */
                    pillEl.addEventListener('blur', function () {
                        if (!fnPinnedNarrow) {
                            var tip = ensureShared();
                            if (tip.dataset.mode === 'peek') { hideTip(); }
                        }
                    });
                    /* Click reads n from data-n — avoids shared loop-variable bug */
                    pillEl.addEventListener('click', function (e) {
                        e.preventDefault();
                        if (tapJustHandled()) return; /* tap already handled by onTap */
                        fnToggle(+pillEl.getAttribute('data-n'));
                    });
                    /* mobile: footnote pill is an href-less <a>, so taps don't reliably
                       fire click — bind touch directly so it opens on the FIRST tap */
                    onTap(pillEl, function () { fnToggle(+pillEl.getAttribute('data-n')); });
                    /* Keyboard: Enter/Space activates the same toggle as click */
                    pillEl.addEventListener('keydown', function (e) {
                        if (e.keyCode === 13 || e.keyCode === 32) {
                            e.preventDefault();
                            var nFromAttr = +pillEl.getAttribute('data-n');
                            fnToggle(nFromAttr);
                        }
                    });
                })(pill, noteText, n);

                /* Build list item */
                var li = document.createElement('li');
                li.id = 'enrich-fn-' + n;
                li.innerHTML = '<span>' + fmt(noteText) + '</span>' +
                    ' <a class="enrich-fnback" href="#enrich-fnsrc-' + n + '" title="back to text">&#8617;</a>';
                ol.appendChild(li);
            }

            if (n === 0) { return; } /* no footnotes on this page */

            /* Back-links: smooth-scroll + flash the source anchor */
            var backs = ol.querySelectorAll('.enrich-fnback');
            for (var b = 0; b < backs.length; b++) {
                (function (back) {
                    back.addEventListener('click', function (e) {
                        e.preventDefault();
                        fnCloseSidenote(); /* F5: close any open margin sidenote */
                        var targetId = back.getAttribute('href').slice(1);
                        var t = document.getElementById(targetId);
                        if (!t) { return; }
                        fnFlash(t);
                        t.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); /* F4: was 'center' (over-scrolled) */
                    });
                })(backs[b]);
            }

            /* Append bottom footnotes section */
            var sec = document.createElement('section');
            sec.className = 'enrich-footnotes';
            var label = document.createElement('div');
            label.className = 'enrich-fn-h';
            label.textContent = 'Footnotes';
            sec.appendChild(label);
            sec.appendChild(ol);
            content.appendChild(sec);
        })();

        /* ---- global dismiss handlers (footnote-specific) --------------- */
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                fnCloseSidenote();
                fnPinNarrow(null);
            }
        });

        document.addEventListener('click', function (e) {
            if (fnPinnedNarrow) {
                var tip = ensureShared();
                if (!e.target.closest || (!e.target.closest('#enrich-tip') && !e.target.closest('.enrich-fnref'))) {
                    fnPinNarrow(null);
                }
            }
        });

        if (!init._fnResizeWired) {
            init._fnResizeWired = true;
            var _fnResizeTimer;
            window.addEventListener('resize', function () {
                clearTimeout(_fnResizeTimer);
                _fnResizeTimer = setTimeout(function () {
                    fnCloseSidenote();
                    fnPinNarrow(null);
                }, 120);
            });
            window.addEventListener('scroll', function () {
                var tip = ensureShared();
                if (tip.dataset.mode === 'peek') { hideTip(); }
            }, { passive: true });
        }
    }

    /* ------------------------------------------------------------------ *
     *  Task 5: tocModel + ensureHeadingIds (pure helpers)                *
     * ------------------------------------------------------------------ */

    /* Wrap every bare <table> (one with no .gh-table ancestor) in
       <div class="gh-table gh-table--auto"> so it inherits the theme's table
       styling. Idempotent — a table already inside .gh-table is skipped, so a
       second run (or a hand-wrapped table) is a no-op. `doc` is passed in for
       testability. Returns the count of tables newly wrapped. */
    function wrapBareTables(content, doc) {
        var tables = content.querySelectorAll('table'), n = 0, i;
        for (i = 0; i < tables.length; i++) {
            var t = tables[i];
            if (t.closest('.gh-table')) { continue; }
            var wrap = doc.createElement('div');
            wrap.className = 'gh-table gh-table--auto';
            t.parentNode.insertBefore(wrap, t);
            wrap.appendChild(t);
            n++;
        }
        return n;
    }

    function tocModel(headings) {
        var min = 6, i, seen = {};
        for (i = 0; i < headings.length; i++) { if (headings[i].level < min) { min = headings[i].level; } }
        var out = [];
        for (i = 0; i < headings.length; i++) {
            var h = headings[i];
            var base = h.id || slug(h.text), id = base, k = 2;
            while (seen[id]) { id = base + '-' + k; k++; }
            seen[id] = true;
            out.push({ level: h.level, text: h.text, id: id, depth: h.level - min });
        }
        return out;
    }

    function ensureHeadingIds(nodes) {
        var seen = {}, i;
        for (i = 0; i < nodes.length; i++) {
            var n = nodes[i];
            if (n.id && n.id.trim() && n.id.indexOf('bc-h-') !== 0) { seen[n.id] = true; continue; }
            var base = slug(n.textContent), id = base, k = 2;
            while (seen[id] || document.getElementById(id)) { id = base + '-' + k; k++; }
            n.id = id; seen[id] = true;
        }
    }

    return {
        slug: slug, fmt: fmt, parseTrigger: parseTrigger, init: init,
        ensureShared: ensureShared, isSelecting: isSelecting, popTip: popTip, hideTip: hideTip,
        tocModel: tocModel, ensureHeadingIds: ensureHeadingIds,
        wrapBareTables: wrapBareTables
    };
});
